import { atomWithQuery } from "jotai-tanstack-query";
import {
  fetchConditions,
  fetchUsers,
  fetchConditionById,
  fetchParentConditions,
  fetchSubConditions,
  fetchUserRoles,
  fetchUserDesignations,
  fetchUserById,
  resendVerifyEmailAddress,
  fetchUserCount,
  fetchSubConditionCount,
  fetchSuperConditionCount,
  fetchMetaUserCount,
  fetchClickAnalyticsWithParams,
} from "../index";
import {
  fetchDistricts,
  fetchHospitals,
} from "../districts";
import {
  cdbContentByIdQueryAtom,
  cdbContentByTypeQueryAtom,
  cpdContentByParamsQueryAtom,
  cpdContentNodesByQueryParamsAtom,
  cpdQuizContentByIdQueryAtom,
  cpdContentCases,
  cpdContentTablesForNodeById,
  cpdContentSubNodesForNodeById,
} from "./learning";
import {
  fetchCbdCoursesById,
  fetchCpdContentCourses,
  getQuizForCourseById,
} from "../courses";

export const conditionsQueryAtom = atomWithQuery(() => ({
  queryKey: ["conditions"],
  queryFn: fetchConditions,
  refetchOnWindowFocus: false,
}));

export const parentConditionsQueryAtom = atomWithQuery(() => ({
  queryKey: ["parentConditions"],
  queryFn: fetchParentConditions,
  refetchOnWindowFocus: false,
}));

export const usersQueryAtom = atomWithQuery(() => ({
  queryKey: ["users"],
  queryFn: fetchUsers,
  refetchOnWindowFocus: false,
}));

export const conditionByIdQueryAtom = (conditionId) =>
  atomWithQuery(() => ({
    queryKey: ["condition", conditionId],
    queryFn: () => fetchConditionById(conditionId),
    // enabled: !!conditionId, // Only run if conditionId is available
    refetchOnWindowFocus: false,
  }));

export const parentConditionSubConditionsQueryAtom = (conditionId) =>
  atomWithQuery(() => ({
    queryKey: ["parentConditionSubConditions", conditionId],
    queryFn: () => fetchSubConditions(conditionId),
    refetchOnWindowFocus: false,
  }));

export const userRolesQueryAtom = atomWithQuery(() => ({
  queryKey: ["userRoles"],
  queryFn: fetchUserRoles,
  refetchOnWindowFocus: false,
}));

export const userDesignationsQueryAtom = atomWithQuery(() => ({
  queryKey: ["userDesignations"],
  queryFn: fetchUserDesignations,
  refetchOnWindowFocus: false,
}));

export const districtsQueryAtom = atomWithQuery(() => ({
  queryKey: ["districts"],
  queryFn: fetchDistricts,
  refetchOnWindowFocus: false,
}));

export const hospitalsQueryAtom = atomWithQuery(() => ({
  queryKey: ["hospitals"],
  queryFn: fetchHospitals,
  refetchOnWindowFocus: false,
}));

export const userByIdQueryAtom = (userId) =>
  atomWithQuery(() => ({
    queryKey: ["userRecord"],
    queryFn: () => fetchUserById(userId),
    refetchOnWindowFocus: false,
  }));

export const resendEmailVerificationQueryAtom = (emailAddress) =>
  atomWithQuery(() => ({
    queryKey: ["resendVerifyEmail", emailAddress],
    queryFn: () => resendVerifyEmailAddress(emailAddress),
    refetchOnWindowFocus: false,
  }));

export const usersCountQueryAtom = atomWithQuery(() => ({
  queryKey: ["usersCount"],
  queryFn: fetchUserCount,
  refetchOnWindowFocus: false,
}));

export const subConditionsCountQueryAtom = atomWithQuery(() => ({
  queryKey: ["subConditionsCount"],
  queryFn: fetchSubConditionCount,
  refetchOnWindowFocus: false,
}));

export const superConditionsCountQueryAtom = atomWithQuery(() => ({
  queryKey: ["superConditionsCount"],
  queryFn: fetchSuperConditionCount,
  refetchOnWindowFocus: false,
}));

export const activeUsersInPeriodQueryAtom = (params) =>
  atomWithQuery(() => ({
    queryKey: ["activeUsersInPeriod", params.from, params.to],
    queryFn: () =>
      fetchMetaUserCount({
        lastLoggedInAfter: params.from,
        lastLoggedInBefore: params.to,
      }),
    refetchOnWindowFocus: false,
  }));

export const clickAnalyticsWithParamsQueryAtom = (params) =>
  atomWithQuery(() => ({
    queryKey: ["clickAnalytics", params],
    queryFn: () => fetchClickAnalyticsWithParams(params),
    refetchOnWindowFocus: false,
  }));

export const coursesQueryAtom = (params) =>
  atomWithQuery(() => ({
    queryKey: ["courses"],
    queryFn: () => fetchCpdContentCourses(params),
    refetchOnWindowFocus: false,
  }));

export const courseByIdQueryAtom = (courseId, params) =>
  atomWithQuery(() => ({
    queryKey: ["courses", courseId],
    queryFn: () => fetchCbdCoursesById(courseId, params),
    refetchOnWindowFocus: false,
  }));

export const getQuizForCourseByIdQueryAtom = (courseId, params) =>
  atomWithQuery(() => ({
    queryKey: ["courses", courseId, "quiz"],
    queryFn: () => getQuizForCourseById(courseId, params),
    refetchOnWindowFocus: false,
  }));

export {
  cdbContentByIdQueryAtom,
  cdbContentByTypeQueryAtom,
  cpdContentByParamsQueryAtom,
  cpdContentNodesByQueryParamsAtom,
  cpdQuizContentByIdQueryAtom,
  cpdContentCases,
  cpdContentTablesForNodeById,
  cpdContentSubNodesForNodeById,
};

export const QueryAtom = {
  cases: {
    getAll: (params) => cpdContentCases({ ...params }),
    getOne: (uid, type) => cpdQuizContentByIdQueryAtom(uid, type),
  },
  nodes: {
    getAll: (name) =>
      cpdContentNodesByQueryParamsAtom(`conditionName=${name}&levelIndex=1`),
  },
  conditions: {
    getAll: () => conditionsQueryAtom,
  },
  courses: {
    getAll: (params) => coursesQueryAtom(params),
    getOne: (courseId, params) => courseByIdQueryAtom(courseId, params),
  },
  guidelines: {
    getAll: () => conditionsQueryAtom,
  },
};
